import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { EuiButton, EuiFlexGroup, EuiFlexItem, EuiFlyoutBody, EuiFlyoutFooter, EuiFlyoutHeader, EuiText, EuiTitle } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { IntervalUnit } from 'api/interfaces'
import { MasterCampaignPlanCampaignBuilderType } from 'api/interfaces/MasterCampaignStub'
import { useAddMasterCampaignStubCampaignBuilderMutation, useEditMasterCampaignStubCampaignBuilderMutation, useGetMasterCampaignPlanQuery, useGetMasterCampaignPlanMasterCampaignStubQuery, useUpsertMasterCampaignPlanOffAdCritterCustomOptionMutation } from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import { getCampaignTypeNameShort } from 'utils/CampaignType'

import { CampaignForm, AddCampaignSectionStep, CampaignFormFields } from './CampaignForm'

export enum EditCampaignFlyoutMode {
  Add = 'add',
  Edit = 'edit'
}

export interface EditCampaignFlyoutProps {
  editMode: EditCampaignFlyoutMode
  selectedCampaignBuilderId?: string
  onClose: () => void
}

const EditCampaignFlyout = ({ editMode, selectedCampaignBuilderId, onClose }: EditCampaignFlyoutProps) => {
  const { masterCampaignPlanId } = useParams<{ masterCampaignPlanId: string }>()
  const { data: masterCampaignStub } = useGetMasterCampaignPlanMasterCampaignStubQuery({ masterCampaignPlanId: masterCampaignPlanId })
  const { data: masterCampaignPlan } = useGetMasterCampaignPlanQuery({ masterCampaignPlanId: masterCampaignPlanId })
  const [newCampaignStep, setNewCampaignStep] = useState<AddCampaignSectionStep>(AddCampaignSectionStep.CampaignType)

  const didFireUpdate = useRef(false)

  const [campaignBuilder, setCampaignBuilder] = useState<CampaignFormFields | undefined>(() => {
    if (editMode === EditCampaignFlyoutMode.Add) {
      return {
        name: 'New Campaign',
        shortSummary: '',
        longSummary: '',
        audienceSummary: '',
        geoSummary: '',
        seasonalitySummary: '',
        campaignType: MasterCampaignPlanCampaignBuilderType.TV,
        retargetSite: false,
        startDate: moment().add(3, 'days'),
        intervalUnit: IntervalUnit.Month,
        intervalFrequency: 1,
        intervalDuration: 1,
        segmentTargets: [],
        zips: []
      }
    }

    const builder = masterCampaignStub!.campaignBuilders.find(builder => builder.id === selectedCampaignBuilderId)
    if (!builder) {
      return undefined
    }
    return {
      name: builder.name,
      shortSummary: builder.shortSummary,
      longSummary: builder.longSummary,
      audienceSummary: builder.audienceSummary,
      geoSummary: builder.geoSummary,
      seasonalitySummary: builder.seasonalitySummary,
      campaignType: builder.campaignType,
      isOffAdCritter: builder.isOffAdCritter,
      offAdCritterName: builder.offAdCritterName,
      offAdCritterColor: builder.offAdCritterColor,
      cpm: builder.cpm,
      marginOfError: builder.marginOfError ? builder.marginOfError * 100 : undefined,
      includeInProjections: builder.includeInProjections,
      includeInGeoHeatmap: builder.includeInGeoHeatmap,
      intervalBudget: builder.intervalBudget,
      retargetSite: builder.retargetSite,
      startDate: builder.startDate!,
      intervalUnit: builder.intervalUnit || IntervalUnit.Month,
      intervalFrequency: builder.intervalFrequency || 1,
      intervalDuration: builder.intervalDuration || 1,
      segmentTargets: builder.segmentTargets,
      zips: builder.zips || []
    }
  })

  const [editMasterCampaignStubCampaignBuilderMutation, { isSuccess: isEditingMasterCampaignStubCampaignBuilderSuccess, isLoading: isEditingMasterCampaignStubCampaignBuilder }] = useEditMasterCampaignStubCampaignBuilderMutation()
  const [addMasterCampaignStubCampaignBuilderMutation, { isSuccess: isAddingMasterCampaignStubCampaignBuilderSuccess, isLoading: isAddingMasterCampaignStubCampaignBuilder }] = useAddMasterCampaignStubCampaignBuilderMutation()
  const [upsertMasterCampaignPlanOffAdCritterCustomOptionMutation, { isLoading: isUpsertingCustomOption }] = useUpsertMasterCampaignPlanOffAdCritterCustomOptionMutation()

  const isSuccess = isEditingMasterCampaignStubCampaignBuilderSuccess || isAddingMasterCampaignStubCampaignBuilderSuccess

  useEffect(() => {
    if (didFireUpdate.current && isSuccess) {
      didFireUpdate.current = false
      onClose()
    }
  }, [didFireUpdate, isSuccess])

  if (!campaignBuilder) {
    return null
  }

  const onSave = async () => {
    if (!masterCampaignPlanId || !masterCampaignStub || !campaignBuilder.intervalBudget) {
      return
    }

    const standardPlatforms = ['Facebook', 'Instagram', 'LinkedIn']
    if (editMode === EditCampaignFlyoutMode.Add && campaignBuilder.isOffAdCritter && campaignBuilder.offAdCritterName && campaignBuilder.offAdCritterColor && !standardPlatforms.includes(campaignBuilder.offAdCritterName)) {
      await upsertMasterCampaignPlanOffAdCritterCustomOptionMutation({
        masterCampaignPlanId: masterCampaignPlanId,
        name: campaignBuilder.offAdCritterName,
        color: campaignBuilder.offAdCritterColor
      })
    }

    if (editMode === EditCampaignFlyoutMode.Add) {
      addMasterCampaignStubCampaignBuilderMutation({
        masterCampaignStubId: masterCampaignStub.id,
        request: {
          ...campaignBuilder,
          intervalBudget: campaignBuilder.intervalBudget,
          marginOfError: campaignBuilder.marginOfError ? campaignBuilder.marginOfError / 100 : undefined
        }
      })
    } else {
      if (!selectedCampaignBuilderId) {
        return
      }
      editMasterCampaignStubCampaignBuilderMutation({
        masterCampaignStubId: masterCampaignStub.id,
        masterCampaignStubCampaignBuilderId: selectedCampaignBuilderId,
        masterCampaignPlanId: masterCampaignPlanId,
        request: {
          ...campaignBuilder,
          intervalBudget: campaignBuilder.intervalBudget,
          marginOfError: campaignBuilder.marginOfError ? campaignBuilder.marginOfError / 100 : undefined
        }
      })
    }

    didFireUpdate.current = true
  }

  const onNext = () => {
    setNewCampaignStep(AddCampaignSectionStep.CampaignDetails)
  }

  const onBack = () => {
    setNewCampaignStep(AddCampaignSectionStep.CampaignType)
  }

  const isValidHexColor = (color: string): boolean => {
    return /^#([0-9A-F]{3}){1,2}$/i.test(color) || color.startsWith('linear-gradient') || color.startsWith('radial-gradient')
  }

  const isSaveDisabled = () => {
    if (!campaignBuilder) return true

    if (campaignBuilder.isOffAdCritter && !campaignBuilder.offAdCritterName) return true
    if (campaignBuilder.isOffAdCritter && (!campaignBuilder.offAdCritterColor || !isValidHexColor(campaignBuilder.offAdCritterColor))) return true

    const budgetValidation = !campaignBuilder.isOffAdCritter ? (!!campaignBuilder.intervalBudget && campaignBuilder.intervalBudget < 100) || !campaignBuilder.intervalBudget : !campaignBuilder.intervalBudget

    const hasProjectionData = !!campaignBuilder.cpm || !!campaignBuilder.marginOfError
    const projectionValidation = campaignBuilder.isOffAdCritter && hasProjectionData && (!campaignBuilder.cpm || campaignBuilder.cpm <= 0 || !campaignBuilder.marginOfError || campaignBuilder.marginOfError <= 0)

    return !campaignBuilder.name || moment(campaignBuilder.startDate).startOf('day').diff(moment().startOf('day'), 'days') < 3 || campaignBuilder.intervalDuration <= 0 || budgetValidation || projectionValidation
  }

  const getCampaignTypeLabel = () => {
    if (campaignBuilder.isOffAdCritter && campaignBuilder.offAdCritterName) {
      return `${campaignBuilder.offAdCritterName}`
    }
    return getCampaignTypeNameShort(campaignBuilder.campaignType)
  }

  return (
    <AcFlyout onClose={onClose}>
      <EuiFlyoutHeader>
        <EuiTitle size={'m'}>
          <h2>{editMode === EditCampaignFlyoutMode.Add ? `Add Campaign` : `Edit ${getCampaignTypeLabel()} Campaign`}</h2>
        </EuiTitle>
      </EuiFlyoutHeader>
      <EuiFlyoutBody>
        {editMode === EditCampaignFlyoutMode.Add ? <CampaignForm campaignBuilder={campaignBuilder} setCampaignBuilder={setCampaignBuilder} step={newCampaignStep} offAdCritterCustomOptions={masterCampaignPlan?.offAdCritterCustomOptions || []} /> : <CampaignForm campaignBuilder={campaignBuilder} setCampaignBuilder={setCampaignBuilder} step={AddCampaignSectionStep.CampaignDetails} offAdCritterCustomOptions={masterCampaignPlan?.offAdCritterCustomOptions || []} />}
      </EuiFlyoutBody>
      <EuiFlyoutFooter>
        {editMode === EditCampaignFlyoutMode.Add ? (
          <EuiFlexGroup direction={'row'} alignItems={'center'}>
            {newCampaignStep === AddCampaignSectionStep.CampaignType && (
              <>
                <EuiFlexItem grow={false}>
                  <EuiButton onClick={onClose}>Cancel</EuiButton>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton fill onClick={onNext}>
                    Next
                  </EuiButton>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiText size='xs'>
                    <p>* Required fields</p>
                  </EuiText>
                </EuiFlexItem>
              </>
            )}
            {newCampaignStep === AddCampaignSectionStep.CampaignDetails && (
              <>
                <EuiFlexItem grow={false}>
                  <EuiButton onClick={onBack}>Back</EuiButton>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton onClick={onSave} isLoading={isAddingMasterCampaignStubCampaignBuilder || isUpsertingCustomOption} fill color={'primary'} isDisabled={isSaveDisabled()}>
                    Save
                  </EuiButton>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiText size='xs'>
                    <p>* Required fields</p>
                  </EuiText>
                </EuiFlexItem>
              </>
            )}
          </EuiFlexGroup>
        ) : (
          <EuiFlexGroup direction={'row'} alignItems={'center'}>
            <EuiFlexItem grow={false}>
              <EuiButton onClick={onClose}>Cancel</EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButton fill onClick={onSave} isLoading={isEditingMasterCampaignStubCampaignBuilder || isUpsertingCustomOption} isDisabled={isSaveDisabled()}>
                Save
              </EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size='xs'>
                <p>* Required fields</p>
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        )}
      </EuiFlyoutFooter>
    </AcFlyout>
  )
}

export default EditCampaignFlyout
